import { QuestionnaireQuestion } from "../../types/questionnaire";
import { Button } from "../../components/ui/button";
import { AlertCircle, Info } from "lucide-react";
import { RadioButton } from "../ui/RadioButton";
import { CustomCheckbox } from "../ui/CustomCheckbox";
import { TextInput } from "../ui/TextInput";
import { CustomTextArea } from "../ui/CustomTextArea";
import { getQuestionDescription, getQuestionTitle, getQuestionPlaceholder } from "../../lib/question-descriptions";
import { getQuestionnaireTranslations } from "../../lib/questionnaire-translations";
import {
  getNoneOfTheAboveValues,
  isNoneOfTheAboveOption,
  sanitizeMultipleOptionAnswer,
  toggleMultipleOptionWithNoneExclusivity,
} from "../../lib/none-of-the-above";
import {
  getRadioTestId,
  getCheckboxTestId,
  getInputTestId,
  getTextareaTestId,
} from "../../lib/test-ids";

// Question IDs that need special handling to extract only the first div (Spanish content only)
const SPANISH_FIRST_DIV_ONLY_QUESTIONS = [
  "2f02cfe6-8365-4c7c-9a62-0958759924c6", // PT-141 informational - has mixed Spanish/English content
];

/**
 * Affirmative "yes" labels from MDI sometimes omit the accent (Si → Sí).
 * Only rewrite standalone yes / "Si - …" option labels — not conditional "Si …" (if).
 */
function withAffirmativeSiAccent(label: string): string {
  const trimmed = label.trim();
  if (/^si$/i.test(trimmed)) {
    return label.replace(/si/i, "Sí");
  }
  if (/^si(\s*[-–—:,])/i.test(trimmed)) {
    return label.replace(/^(\s*)si/i, "$1Sí");
  }
  return label;
}
/**
 * Extract only the first child div from HTML content
 * Used for questions that have mixed language content where we only want the Spanish part
 */
function extractFirstDiv(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const container = doc.body.firstChild as HTMLElement;
  const firstDiv = container?.querySelector(":scope > div");
  return firstDiv?.outerHTML || html;
}

interface QuestionRendererProps {
  question: QuestionnaireQuestion;
  value: string | string[] | boolean | { option: string; textarea: string } | undefined;
  onChange: (value: string | string[] | boolean | { option: string; textarea: string }) => void;
  error?: string;
  language?: "en" | "es";
  disabled?: boolean;
}

export function QuestionRenderer({
  question,
  value,
  onChange,
  error,
  language = "en",
  disabled = false,
}: QuestionRendererProps) {
  const questionId = `question-${question.partner_questionnaire_question_id}`;
  const errorId = `${questionId}-error`;
  const descriptionId = `${questionId}-description`;
  const t = getQuestionnaireTranslations(language);
  // Prefer hardcoded overrides — MDI often sends English placeholders on ES questionnaires
  const displayPlaceholder =
    getQuestionPlaceholder(question.partner_questionnaire_question_id, language) ||
    question.placeholder ||
    undefined;

  const renderQuestionContent = () => {
    switch (question.type) {
      case "boolean":
        return (
          <div className="flex flex-col gap-4">
            <Button
              type="button"
              variant={value === true ? "default" : "outline"}
              onClick={() => onChange(true)}
              disabled={disabled}
              className="h-auto py-4 text-base"
            >
              {t.yes}
            </Button>
            <Button
              type="button"
              variant={value === false ? "default" : "outline"}
              onClick={() => onChange(false)}
              disabled={disabled}
              className="h-auto py-4 text-base"
            >
              {t.no}
            </Button>
          </div>
        );

      case "string":
        return (
          <TextInput
            value={(value as string) || ""}
            onChange={onChange}
            placeholder={displayPlaceholder}
            testId={getInputTestId(question.partner_questionnaire_question_id)}
          />
        );
      case "text":
        return (
          <CustomTextArea
            value={(value as string) || ""}
            onChange={onChange}
            placeholder={displayPlaceholder}
            testId={getTextareaTestId(question.partner_questionnaire_question_id)}
          />
        );
      case "textarea":
        const textareaId = `${questionId}-textarea`;
        return (
          <div className="space-y-3">
            {question.label && (
              <label htmlFor={textareaId} className="block text-sm font-medium text-black required-label">
                {question.label}
              </label>
            )}
            <CustomTextArea
              id={textareaId}
              value={(value as string) || ""}
              onChange={onChange}
              placeholder={displayPlaceholder}
              className="!h-[135px] !min-h-[135px]"
              required={!question.is_optional}
              testId={getTextareaTestId(question.partner_questionnaire_question_id)}
            />
          </div>
        );

      case "single_option":
        return (
          <div className="flex flex-col gap-4">
            {question.options
              .sort((a, b) => a.order - b.order)
              .map((option) => (
                <RadioButton
                  key={option.partner_questionnaire_question_option_id}
                  label={language === "es" ? withAffirmativeSiAccent(option.title) : option.title}
                  value={option.option}
                  checked={value === option.option}
                  onChange={onChange}
                  disabled={disabled}
                  testId={getRadioTestId(question.partner_questionnaire_question_id, option.option)}
                />
              ))}
          </div>
        );

      case "single_option_with_textarea":
        const selectedOption = question.options.find(
          opt => opt.option === (typeof value === 'object' && value !== null && 'option' in value ? (value as any).option : value)
        );
        const showTextarea = selectedOption?.is_show_additional_field || false;
        const textareaValue = typeof value === 'object' && value !== null && 'textarea' in value
          ? (value as any).textarea
          : '';

        return (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              {question.options
                .sort((a, b) => a.order - b.order)
                .map((option) => (
                  <RadioButton
                    key={option.partner_questionnaire_question_option_id}
                    label={language === "es" ? withAffirmativeSiAccent(option.title) : option.title}
                    value={option.option}
                    checked={(typeof value === 'object' && value !== null && 'option' in value ? (value as any).option : value) === option.option}
                    onChange={(selectedValue) => {
                      // When option changes, preserve textarea value if switching to "Yes"
                      onChange({
                        option: selectedValue,
                        textarea: option.is_show_additional_field ? textareaValue : ''
                      });
                    }}
                    disabled={disabled}
                    testId={getRadioTestId(question.partner_questionnaire_question_id, option.option)}
                  />
                ))}
            </div>

            {showTextarea && (
              <div className="space-y-3 animate-fade-in">
                <label htmlFor={`${questionId}-additional-textarea`} className="block text-sm font-medium text-black required-label">
                  {question.label}
                </label>
                <CustomTextArea
                  id={`${questionId}-additional-textarea`}
                  value={textareaValue}
                  onChange={(newTextareaValue) => {
                    onChange({
                      option: (typeof value === 'object' && value !== null && 'option' in value ? (value as any).option : value),
                      textarea: newTextareaValue
                    });
                  }}
                  placeholder={displayPlaceholder}
                  className="!h-[155px] !min-h-[155px]"
                  required={!question.is_optional}
                  testId={getTextareaTestId(question.partner_questionnaire_question_id)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        );

      case "multiple_option": {
        const noneOptionValues = getNoneOfTheAboveValues(question.options);
        const selectedValues = sanitizeMultipleOptionAnswer(
          (value as string[]) || [],
          noneOptionValues
        );

        return (
          <div className="flex flex-col gap-4">
            {question.options
              .sort((a, b) => a.order - b.order)
              .map((option, index) => {
                const isChecked = selectedValues.includes(option.option);
                const label =
                  language === "es" ? withAffirmativeSiAccent(option.title) : option.title;
                const applyToggle = (checked: boolean) => {
                  onChange(
                    toggleMultipleOptionWithNoneExclusivity(
                      selectedValues,
                      option.option,
                      checked,
                      noneOptionValues
                    )
                  );
                };

                // Exclusive "none of the above" uses round radio style; other options stay checkboxes.
                // Like every other RadioButton in this file, clicking always selects — it never
                // toggles off on re-click, so re-tapping an already-selected "none" re-affirms the
                // answer (and re-fires the auto-advance) instead of clearing the selection.
                if (isNoneOfTheAboveOption(option)) {
                  return (
                    <RadioButton
                      key={option.partner_questionnaire_question_option_id}
                      label={label}
                      value={option.option}
                      checked={isChecked}
                      onChange={() => applyToggle(true)}
                      disabled={disabled}
                      testId={getRadioTestId(
                        question.partner_questionnaire_question_id,
                        option.option
                      )}
                    />
                  );
                }

                return (
                  <CustomCheckbox
                    key={option.partner_questionnaire_question_option_id}
                    label={label}
                    value={option.option}
                    checked={isChecked}
                    onChange={applyToggle}
                    testId={getCheckboxTestId(question.partner_questionnaire_question_id, index + 1)}
                  />
                );
              })}
          </div>
        );
      }

      case "informational":
        // For certain questions with mixed language content, extract only the first div (Spanish)
        const shouldExtractFirstDiv =
          language === "es" &&
          SPANISH_FIRST_DIV_ONLY_QUESTIONS.includes(question.partner_questionnaire_question_id);
        const informationalDescription = shouldExtractFirstDiv
          ? extractFirstDiv(question.description || "")
          : question.description || "";

        // Don't render the info box if there's no description content
        if (!informationalDescription.trim()) {
          return null;
        }

        return (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-6" role="region" aria-labelledby={questionId}>
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <div
                  className="text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:pl-1"
                  dangerouslySetInnerHTML={{
                    __html: informationalDescription,
                  }}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Get hardcoded overrides (title and description)
  // For English: falls back to MDI data if no override exists
  // For Spanish: only use hardcoded overrides (MDI often sends English descriptions)
  const hardcodedTitle = getQuestionTitle(
    question.partner_questionnaire_question_id,
    language
  );
  const hardcodedDescription = getQuestionDescription(
    question.partner_questionnaire_question_id,
    language
  );
  const displayTitle = hardcodedTitle || question.title;
  const displayDescription = language === "en"
    ? (hardcodedDescription || question.description || null)
    : (hardcodedDescription || null);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2.5">
        <h1
          id={questionId}
          className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal text-black"
          style={{ fontFamily: "TT Commons Pro" }}
        >
          {displayTitle}
        </h1>
        {displayDescription && question.type !== "informational" && (
          <div
            id={descriptionId}
            className="text-[16px] leading-[22px] font-medium text-gray-500 [&_ol]:list-disc [&_ol]:pl-5 [&_ol]:space-y-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:pl-1"
            style={{ fontFamily: "TT Commons Pro", fontWeight: 500 }}
            dangerouslySetInnerHTML={{ __html: displayDescription }}
          />
        )}
      </div>

      <div
        role={question.type === "informational" ? undefined : "group"}
        aria-labelledby={questionId}
        aria-describedby={displayDescription ? descriptionId : undefined}
        aria-invalid={!!error}
        aria-errormessage={error ? errorId : undefined}
        aria-required={!question.is_optional}
      >
        {renderQuestionContent()}
      </div>

      {error && (
        <div
          id={errorId}
          className="text-red-500 text-sm flex items-center gap-2 animate-fade-in"
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle className="w-4 h-4" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
